import React from "react";
import { Link } from "react-router-dom";
import { withToastManager } from "react-toast-notifications";
import BrandLinksRow from "../../Helper/BrandLinksRow";
import Helper from "../../Helper/helper";
import { fetchOrganizationBySlug } from "../../Helper/organizations";
import ToastDemo from "../../Helper/toaster";
import EmptyState from "../../Dashboard/EmptyState";
import OrganizationActionBar from "./OrganizationActionBar";
import RichTextContent from "../../Helper/RichTextContent";
import ExperienceGate from "../../Experience/ExperienceGate";
import {
  resolveMediaUrl,
  resolveMediaBackground,
  handleMediaImgError,
} from "../../Helper/mediaUrl";
import ActivityFeedSection from "../../Community/ActivityFeedSection";
import CommunityFollowButton from "../../Community/CommunityFollowButton";
import { fetchCommunityFollows } from "../../Community/communitySocialApi";
import {
  fetchShopProducts,
  formatShopPrice,
} from "../../Shop/shopApi";
import ShareMenu from "../../Helper/ShareMenu";
import EntityIdentity from "../../Helper/EntityIdentity";
import { AppearanceButton } from "../../Appearance";
import {
  normalizeSocialNetworkKey,
  socialNetworkMeta,
  extractCustomIconUrl,
} from "../../Helper/socialNetworks";
import { shouldRenderEntityStorefrontSection, filterEntityPublicShopProducts } from "./entityStorefrontVisibility";
import EntityPublicVideoLibrary from "./EntityPublicVideoLibrary";
import "./OrganizationPublicPage.css";
import "../../Community/CommunitySocial.css";

/**
 * Public Organization homepage — canonical /organization/:slug
 * Aliases: /organizations/:slug; /org/:slug redirects to canonical.
 * Layout: cover → identity+About → actions → Activity | Videos+Storefront
 */
class OrganizationPublicPage extends Helper {
  state = {
    loading: true,
    organization: null,
    error: false,
    isFollowing: false,
    products: [],
    productsLoading: false,
    aboutExpanded: false,
    videoDetailsFirst: null,
    redirect: false,
    redirectPPV: false,
    redirectPaymentOption: false,
    redirectSubscription: false,
    playButtonClicked: false,
  };

  componentDidMount() {
    this.setState({ playButtonClicked: false });
    this.loadOrganization();
  }

  componentDidUpdate(prevProps) {
    const prevSlug =
      prevProps.match && prevProps.match.params
        ? prevProps.match.params.slug
        : "";
    const nextSlug =
      this.props.match && this.props.match.params
        ? this.props.match.params.slug
        : "";
    if (prevSlug !== nextSlug) {
      this.setState({ playButtonClicked: false });
      this.loadOrganization();
    }
  }

  loadOrganization = async () => {
    const slug =
      this.props.match && this.props.match.params
        ? this.props.match.params.slug
        : "";
    this.setState({
      loading: true,
      error: false,
      isFollowing: false,
      products: [],
    });
    const organization = await fetchOrganizationBySlug(slug);
    let isFollowing = !!(organization && organization.is_following);
    if (organization && organization.id && !isFollowing) {
      const hasSession =
        localStorage.getItem("userId") && localStorage.getItem("accessToken");
      if (hasSession) {
        const follows = await fetchCommunityFollows("organization");
        if (!follows.error) {
          isFollowing = (follows.follows || []).some(
            (row) => Number(row.followable_id) === Number(organization.id)
          );
        }
      }
    }
    this.setState({
      loading: false,
      organization,
      error: !organization,
      isFollowing,
    });
    if (organization && organization.id) {
      this.loadProducts(organization.id);
    }
  };

  loadProducts = async (organizationId) => {
    this.setState({ productsLoading: true });
    const result = await fetchShopProducts({
      organization_id: organizationId,
      per_page: 24,
    });
    this.setState({
      products: (result && result.products) || [],
      productsLoading: false,
    });
  };

  handlePlayVideo = async (event, adminVideoId) => {
    event.preventDefault();

    const inputData = {
      admin_video_id: adminVideoId,
      skip: 0,
    };

    var details = await this.onlySingleVideoFirst(inputData);

    if (!details) {
      ToastDemo(
        this.props.toastManager,
        (this.state.videoDetailsError &&
          this.state.videoDetailsError.error_messages) ||
          "Unable to start playback",
        "error"
      );
      return;
    }

    this.redirectStatus(details);
    this.setState({ playButtonClicked: true });
  };

  /**
   * Organization Social Links only — never platform Brand Links list.
   * Icon priority is resolved in BrandLinksRow / socialNetworks helpers.
   */
  getBrandLinks(organization) {
    if (!organization) {
      return [];
    }

    const fromPayload =
      organization.brand_links &&
      Array.isArray(organization.brand_links.links)
        ? organization.brand_links.links
        : [];

    const filtered = fromPayload.filter(
      (link) =>
        link &&
        link.url &&
        link.is_enabled !== false &&
        link.is_visible !== false
    );
    if (filtered.length) {
      return filtered;
    }

    // Client fallback: org contact.social_links only (preserve custom icons)
    const social =
      (organization.contact && organization.contact.social_links) ||
      organization.social_links ||
      {};
    if (!social || typeof social !== "object") {
      return [];
    }

    const built = [];
    let order = 10;
    Object.keys(social).forEach((key) => {
      const network = normalizeSocialNetworkKey(key);
      const value = social[key];
      const url = String(
        value && typeof value === "object" ? value.url || "" : value || ""
      ).trim();
      if (!url) {
        return;
      }
      const meta = socialNetworkMeta(network);
      const iconUrl =
        value && typeof value === "object" ? extractCustomIconUrl(value) : null;
      const item = {
        id: "org-social-client-" + (network || "link") + "-" + order,
        display_name:
          (value && value.display_name) || meta.label || network,
        url,
        builtin_icon:
          (value && value.builtin_icon) ||
          meta.builtin_icon ||
          "fas fa-link",
        display_order: order,
        open_in_new_tab: true,
        is_enabled: true,
        is_visible: true,
        css_class: meta.css_class || network,
      };
      if (iconUrl) {
        item.icon_type = "custom";
        item.icon_url = iconUrl;
      }
      if (value && typeof value === "object") {
        if (value.custom_icon) item.custom_icon = value.custom_icon;
        if (value.brand_link_id) item.brand_link_id = value.brand_link_id;
        if (value.icon_path) item.icon_path = value.icon_path;
      }
      built.push(item);
      order += 10;
    });
    return built;
  }

  renderLogo(organization) {
    if (organization.logo_path) {
      return (
        <img
          src={resolveMediaUrl(organization.logo_path)}
          alt={organization.name || "Organization"}
          className="org-logo"
          onError={handleMediaImgError}
          loading="lazy"
        />
      );
    }

    return (
      <div
        className="org-logo-placeholder"
        aria-hidden="true"
        title={organization.name || "Organization"}
      >
        <i className="fas fa-building" />
      </div>
    );
  }

  renderVideoCarousel(videos, videoSubcategories) {
    return (
      <EntityPublicVideoLibrary
        videos={videos}
        videoSubcategories={videoSubcategories}
        onPlayVideo={this.handlePlayVideo}
        keyPrefix="org"
      />
    );
  }

  renderStorefront(products) {
    const visibleProducts = filterEntityPublicShopProducts(products);
    if (!visibleProducts.length) {
      return null;
    }

    return (
      <div className="org-storefront-grid">
        {visibleProducts.map((product) => {
          const priceLabel = product.is_free
            ? "Free"
            : formatShopPrice(product.effective_price);
          return (
            <Link
              key={product.id}
              className="org-storefront-card"
              to={`/shop/product/${encodeURIComponent(product.slug)}`}
            >
              <div className="org-storefront-card__media">
                {product.primary_image ? (
                  <img
                    src={product.primary_image}
                    alt={product.name || ""}
                    loading="lazy"
                    onError={handleMediaImgError}
                  />
                ) : (
                  <div className="org-storefront-card__placeholder" />
                )}
              </div>
              <div className="org-storefront-card__body">
                <strong>{product.name}</strong>
                {priceLabel ? <span>{priceLabel}</span> : null}
              </div>
            </Link>
          );
        })}
      </div>
    );
  }

  renderAbout(organization) {
    const html = organization.description || "";
    const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const PREVIEW_LEN = 280;
    const needsCollapse = plain.length > PREVIEW_LEN;
    const expanded = this.state.aboutExpanded;

    let previewText = plain;
    if (needsCollapse && !expanded) {
      let cut = plain.slice(0, PREVIEW_LEN);
      const lastSpace = cut.lastIndexOf(" ");
      if (lastSpace > Math.floor(PREVIEW_LEN * 0.55)) {
        cut = cut.slice(0, lastSpace);
      }
      previewText = cut.trimEnd() + "…";
    }

    return (
      <section
        className="org-about-section"
        aria-labelledby="org-about-heading"
      >
        <h2 id="org-about-heading" className="org-videos-heading">
          About
        </h2>
        <div className="org-about-body">
          {needsCollapse && !expanded ? (
            <p className="org-description">{previewText}</p>
          ) : html ? (
            <RichTextContent html={html} className="org-description rich-text" />
          ) : (
            <p className="org-empty-inline">
              Welcome to {organization.name}.
            </p>
          )}
        </div>
        {needsCollapse ? (
          <AppearanceButton
            variant="readmore"
            type="button"
            className="org-about-toggle"
            onClick={() =>
              this.setState({ aboutExpanded: !this.state.aboutExpanded })
            }
          >
            {expanded ? "Read Less" : "Read More"}
          </AppearanceButton>
        ) : null}
      </section>
    );
  }

  render() {
    const {
      loading,
      organization,
      error,
      playButtonClicked,
      products,
      productsLoading,
    } = this.state;

    if (playButtonClicked) {
      const returnToVideo = this.renderRedirectPage(
        this.state.videoDetailsFirst
      );
      if (returnToVideo != null) {
        return returnToVideo;
      }
    }

    if (loading) {
      return (
        <div className="main-sec-content" style={{ padding: "3rem 1rem" }}>
          <EmptyState
            title="Loading…"
            message="Loading organization profile."
            statusRole="status"
          />
        </div>
      );
    }

    if (error || !organization) {
      return (
        <div className="main-sec-content" style={{ padding: "3rem 1rem" }}>
          <EmptyState
            title="Organization not found"
            message="We couldn't find that organization. It may have been removed or the link is incorrect."
          >
            <Link to="/home" className="ae-btn ae-btn--primary">
              Browse Home
            </Link>
          </EmptyState>
        </div>
      );
    }

    const experience =
      organization.experience || { type: "none", should_present: false };
    const hasCover = Boolean(organization.cover_image_path);
    const brandLinks = this.getBrandLinks(organization);
    const coverStyle = hasCover
      ? {
          backgroundImage: resolveMediaBackground(organization.cover_image_path),
        }
      : undefined;
    const typeLabel =
      organization.organization_type_label ||
      organization.type_label ||
      organization.organization_type ||
      "Organization";

    return (
      <ExperienceGate destinationType="organization" presentation={experience}>
        <div className="organization-public-page">
          {hasCover ? (
            <div
              className="org-hero org-hero--cover"
              style={coverStyle}
              role="img"
              aria-label={(organization.name || "Organization") + " cover"}
            />
          ) : (
            <div className="org-hero org-hero--plain" />
          )}

          <div className="org-page-shell">
            <header className="org-header org-header--identity">
              <div className="org-header-inner">
                {this.renderLogo(organization)}
                <EntityIdentity
                  name={organization.name}
                  handle={organization.slug}
                  typeLabel={typeLabel}
                  entity={organization}
                />
              </div>

              <div className="org-actions-block">
                <div
                  className="org-action-bar org-action-bar--primary"
                  role="group"
                  aria-label="Organization actions"
                >
                  <CommunityFollowButton
                    type="organization"
                    id={organization.id}
                    initiallyFollowing={
                      this.state.isFollowing || !!organization.is_following
                    }
                    followerCount={
                      typeof organization.follower_count === "number"
                        ? organization.follower_count
                        : null
                    }
                    showCount
                    className="ae-btn ae-btn--secondary ae-btn--entity-follow org-action-btn org-follow-btn"
                    onChange={(detail) => {
                      this.setState({
                        isFollowing: !!(detail && detail.following),
                        organization: {
                          ...organization,
                          is_following: !!(detail && detail.following),
                          follower_count:
                            detail && typeof detail.follower_count === "number"
                              ? detail.follower_count
                              : organization.follower_count,
                        },
                      });
                    }}
                  />
                  {organization.donation_mode === "external" &&
                  organization.donation_url ? (
                    <AppearanceButton
                      href={organization.donation_url}
                      variant="donate"
                      className="org-action-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Donate
                    </AppearanceButton>
                  ) : (
                    <AppearanceButton
                      to={`/organization/${organization.slug || organization.id}/donate`}
                      variant="donate"
                      className="org-action-btn"
                    >
                      Donate
                    </AppearanceButton>
                  )}
                  <OrganizationActionBar
                    organizationName={organization.name}
                    website={organization.website}
                    donationUrl={null}
                    extraActions={[]}
                  />
                  <ShareMenu
                    title={organization.name}
                    text={`Check out ${organization.name}`}
                    className="org-action-btn"
                    variant="share"
                  />
                </div>
                {brandLinks.length ? (
                  <BrandLinksRow
                    links={brandLinks}
                    className="social-share org-brand-links"
                  />
                ) : null}
              </div>
            </header>

            <div className="org-content-layout org-content-layout--home">
              <div className="org-content-main">
                <ActivityFeedSection
                  contextType="organization"
                  organizationId={organization.id}
                  title="Activity"
                  headingId="org-activity-heading"
                  className="org-activity-feed"
                />
              </div>

              <aside
                className="org-content-sidebar"
                aria-label="Organization about, media, and storefront"
              >
                {this.renderAbout(organization)}

                <section
                  className="org-videos-section"
                  aria-labelledby="org-videos-heading"
                >
                  <h2 id="org-videos-heading" className="org-videos-heading">
                    Latest Videos
                  </h2>
                  {this.renderVideoCarousel(
                    organization.videos,
                    organization.video_subcategories
                  )}
                </section>

                {shouldRenderEntityStorefrontSection({
                  products,
                  productsLoading,
                }) ? (
                  <section
                    className="org-storefront-section"
                    aria-labelledby="org-storefront-heading"
                  >
                    <h2
                      id="org-storefront-heading"
                      className="org-videos-heading"
                    >
                      Storefront
                    </h2>
                    {this.renderStorefront(products)}
                  </section>
                ) : null}
              </aside>
            </div>
          </div>
        </div>
      </ExperienceGate>
    );
  }
}

export default withToastManager(OrganizationPublicPage);
